import type { Foglio, LogoBreve, PdfRichiesto } from './stampa.ts'
import type { FirmatarioNoto } from './regole-consensi.ts'

/**
 * Forme del modulo del destinatario dei progetti (492). Modello, versioni FAMI e foglio li compone
 * l'API (apps/api/src/servizi/modulo-progetto.ts): la pagina del progetto modifica il modello, la
 * pagina di stampa e l'anteprima impaginano il foglio come lo riceve anche il PDF/A.
 */

export type CampoModulo =
  | 'nome_cognome' | 'data_nascita' | 'luogo_nascita' | 'genere' | 'codice_fiscale' | 'nazionalita'
  | 'permesso_soggiorno' | 'titolo_studio' | 'lavoro_paese_origine' | 'telefono' | 'email'

export type VoceModulo =
  | { tipo: 'campo'; campo: CampoModulo; obbligatorio: boolean; etichetta?: string | null }
  | { tipo: 'testo'; testo: string }
  | { tipo: 'vulnerabilita'; testo: string }
  | { tipo: 'note'; etichetta: string; obbligatorio: boolean }

export type VersioneFami = 'fami_essenziale' | 'fami_completa'

export type ModelloModulo = {
  modello: VersioneFami | null
  sopratitolo: string | null
  fondo: string | null
  titolo_scheda: string
  codice_identificativo: string
  beneficiario_finale: string
  titolo_progetto: string
  loghi: string[]
  voci: VoceModulo[]
  informativa: string
  titolare: string
  allegati: string[]
}

export type RigaFoglioModulo =
  | {
      tipo: 'campo'; campo: CampoModulo; numero: number | null; etichetta: string; facoltativo: boolean
      celle: { etichetta: string | null; valore: string | null }[]
      segnaposto: string | null
      caselle: { voce: string; segnata: boolean }[] | null
    }
  | { tipo: 'testo'; testo: string }
  | { tipo: 'vulnerabilita'; testo: string; voci: (string | null)[] }
  | { tipo: 'note'; numero: number | null; etichetta: string; facoltativo: boolean }

export type FoglioModulo = {
  versione: 1
  progetto: string
  sopratitolo: string | null
  fondo: string | null
  titolo_scheda: string
  intestazione: { codice: string; beneficiario: string; titolo: string }
  righe: RigaFoglioModulo[]
  facoltativi: boolean
  informativa: string
  allegati: string[]
  compilato: boolean
}

export type LogoDelProgetto = LogoBreve & { id: string }

/** GET /progetti/:id/modulo-destinatario. */
export type ModuloDelProgetto = {
  progetto: { id: string; titolo: string; codice_cidis: string | null; nome_breve: string }
  modulo: ModelloModulo | null
  aggiornato: { il: string; da: string | null } | null
  gestisce: boolean
  loghi_progetto: LogoDelProgetto[]
  loghi_tolti: number
  anteprima: FoglioModulo | null
  loghi_anteprima: LogoDelProgetto[]
  modelli: Record<VersioneFami, ModelloModulo> | null
  campi: { codice: CampoModulo; etichetta: string }[]
  segnaposti: { codice: string; descrizione: string }[]
}

/**
 * Il modulo dei consensi di CIDIS come firma da raccogliere (495), dal registro dei consensi: da firmare
 * finche' i consensi 1 e 2 non sono registrati; firmato, con la data, e la scansione caricata o no.
 */
export type StatoModuloCidis = {
  stato: 'firmato' | 'da_firmare'
  /** AAAA-MM-GG. */
  data: string | null
  scansione: 'caricata' | 'da_caricare' | null
}

/** GET /persone/:id/moduli-progetto: i progetti della persona con un modulo, e i moduli firmati. */
export type ModuliDellaScheda = {
  progetti: {
    id: string; codice_cidis: string | null; titolo: string; codice: string; nome_breve: string; aperto: boolean
    /** Data della firma del modulo del progetto (495), AAAA-MM-GG; nulla se manca. */
    firmato_il: string | null
    /** Nullo per chi non legge i documenti della scheda. */
    firmati: { id: string; data_firma: string; antivirus: string; caricato_il: string }[] | null
  }[]
  /** Scheda con dati anonimizzati: niente moduli da firmare. */
  anonimizzata: boolean
  /** Il modulo dei consensi di CIDIS, la seconda firma; nullo per chi non legge i consensi della scheda. */
  cidis: StatoModuloCidis | null
  stampare: boolean
  motivo: string | null
  caricare: boolean
}

/** GET /persone/:id/moduli-progetto/:progetto. */
export type ModuloCompilato = {
  foglio: FoglioModulo
  loghi: LogoDelProgetto[]
  progetto: { id: string; titolo: string; codice_cidis: string | null; nome_breve: string }
  numero_scheda: number
  etichetta: string
}

/**
 * GET /persone/:id/moduli-progetto/:progetto/due-moduli (495): il modulo del progetto e il modulo dei
 * consensi di CIDIS, la scheda utente da firmare con il progetto in intestazione e i suoi loghi.
 */
export type DueModuli = ModuloCompilato & {
  cidis: { foglio: Foglio; riga_firmatario: string | null; firmatario: FirmatarioNoto | null; loghi: LogoBreve[] }
}

/** Nell'elenco dei PDF del modulo: `due_moduli` se il PDF ha anche il modulo CIDIS. */
export type PdfModulo = PdfRichiesto & { due_moduli: boolean }

export type { PdfRichiesto }

/** Tipo di documento del modulo firmato (492): non e' un modulo dei consensi di CIDIS. */
export const TIPO_MODULO_FIRMATO = 'MODULO_PROGETTO_FIRMATO'

export const NOMI_VERSIONI: Record<VersioneFami, { nome: string; descrizione: string }> = {
  fami_essenziale: {
    nome: 'Essenziale',
    descrizione: 'Come «Le cosmonaute»: nome e cognome, data di nascita, genere, codice fiscale, nazionalità e permesso '
      + 'di soggiorno per i cittadini di Paesi terzi; si allegano documento d’identità e permesso di soggiorno.',
  },
  fami_completa: {
    nome: 'Completa',
    descrizione: 'Come LUMEN: i sei dati obbligatori, due celle per le vulnerabilità e, facoltativi, titolo di studio, '
      + 'lavoro nel Paese di origine, telefono, e-mail e note.',
  },
}
